/**
 * ChatCTABlockRenderer
 *
 * Renders a chat_cta block element with a "Chat Now" button.
 * On click, triggers a new chat workflow execution and navigates to it
 * with the behaviour's prompt, platform context, and report blocks.
 */

import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatCTABlock } from '@strivelabs/ui-blocks';
import { toast } from '@strivelabs/ui-core';
import { useChatWorkflow } from '@/hooks/useChatWorkflow';
import { useAppStore } from '@/store/appStore';
import { getChatWorkflowInstanceId } from '@/utils/featureFlags';
import { wrapActionAsAutoTriggeredBlock } from '@/utils/cardBehaviourUtils';
import { startChatHandoff, navigateToChatWithPayload } from '@/utils/chatHandoff';
import { MOCK_EXECUTION_ID } from '@/constants/mockBehaviourChatData';
import { DEFAULT_REPORT_PROMPT } from '@/constants/reports';
import type { BlockElement, ChatCTAContent, BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { log } from "@/lib/logger"

export interface ChatCTABlockRendererProps {
  element: BlockElement;
}

export const ChatCTABlockRenderer: React.FC<ChatCTABlockRendererProps> = ({ element }) => {
  const navigate = useNavigate();
  const { mutateAsync: triggerChatWorkflow } = useChatWorkflow();
  const workflowInstanceId = useAppStore(
    (state) => getChatWorkflowInstanceId(state.agentInstances)
  );

  const content = element.content as ChatCTAContent;
  const behaviour = useMemo(
    () => element.behaviours?.find(
      (b) => b.trigger === 'click' && b.action.type === 'agent_experience'
    ),
    [element.behaviours]
  );

  const buildPayload = useCallback((action: BehaviourAgentExperienceAction) => {
    const message = action.data?.title || DEFAULT_REPORT_PROMPT;
    // wrapActionAsAutoTriggeredBlock expects AgentExperienceAction (title required).
    // Provide the resolved title so the types align.
    const actionWithTitle = { ...action, data: { ...action.data, title: message } };
    const wrappedBlock = wrapActionAsAutoTriggeredBlock(actionWithTitle);
    const platformContext = action.data?.platform_context as Record<string, unknown> | undefined;
    return { message, blocks: [wrappedBlock], platformContext };
  }, []);

  const handleChatClick = useCallback(async () => {
    if (!behaviour) return;

    const action = behaviour.action as BehaviourAgentExperienceAction;
    const payload = buildPayload(action);

    // When MOCK_EXECUTION_ID is set (via env), route directly to it
    // without triggering a real workflow — useful for demo/testing.
    if (MOCK_EXECUTION_ID) {
      navigateToChatWithPayload(navigate, MOCK_EXECUTION_ID, payload);
      return;
    }

    if (!workflowInstanceId) {
      toast({
        title: 'Chat unavailable',
        description: 'Unable to start a chat session. Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await startChatHandoff({
        triggerChatWorkflow,
        navigate,
        workflowInstanceId,
        payload,
      });
    } catch (error) {
      log('error', 'Failed to start chat session.', { error });
      toast({
        title: 'Error',
        description: 'Failed to start chat session. Please try again.',
        variant: 'destructive',
      });
    }
  }, [behaviour, workflowInstanceId, triggerChatWorkflow, navigate, buildPayload]);

  return (
    <ChatCTABlock
      context={content.context}
      buttonText={content.button_text}
      onChatClick={handleChatClick}
    />
  );
};
